<template>
  <div v-click-outside="handleClose" :class="rootStyles">
    <div class="color-picker__items">
      <button
        v-for="colorOption in prepareColors"
        :key="colorOption.value"
        type="button"
        :style="{ backgroundColor: colorOption.value }"
        :class="{
          [`color-picker__item color-picker__item--${size}`]: true,
          'color-picker__item--selected': colorOption.value === value
        }"
        @click.prevent="handleSelect(colorOption.value)"
      >
        <div v-if="showColorLabel" class="color-picker__color-name">
          {{ colorOption.name }}
        </div>
        <UseSvg
          v-if="colorOption.value === value"
          icon="checkbox"
          class="color-picker__checkbox"
          :style="getContrastingColor(colorOption.value)"
        />
      </button>

      <button
        type="button"
        :class="{
          [`color-picker__item color-picker__item--custom color-picker__item--${size}`]: true,
          'color-picker__item--selected': isCustomSelected(value)
        }"
        @click.prevent="handleOpen"
      >
        <UseSvg icon="custom-color" class="color-picker__icon" />
        <UseSvg v-if="isCustomSelected(value)" icon="checkbox" class="color-picker__checkbox" />
      </button>
    </div>

    <Chrome v-if="isInline" v-model="color" :disable-alpha="true" class="color-picker__selector" />

    <div
      v-else-if="isOpened"
      class="color-picker__modal"
      :class="{ [`color-picker__modal--${modalPosition}`]: modalPosition }"
    >
      <div class="color-picker__backdrop" @click.prevent="handleClose"></div>

      <div class="color-picker__content">
        <div class="color-picker__header">
          Select custom color
          <UseSvg class="color-picker__close" icon="menu-close" @click.prevent="handleClose" />
        </div>

        <slot name="picker-preview" :color="color" />

        <Chrome v-model="color" :disable-alpha="true" class="color-picker__selector" />

        <div class="color-picker__actions">
          <button type="button" class="color-picker__cancel" @click.prevent="handleClose">
            Cancel
          </button>
          <button
            type="button"
            :disabled="disableApply"
            :class="{
              'color-picker__apply': true,
              'color-picker__apply--disabled': disableApply
            }"
            @click.prevent="handleApply"
          >
            Apply color
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { Chrome } from "@ckpack/vue-color";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { getContrastingColor } from "components/jiffy/vue-product-details-dtf/utils";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";

const props = defineProps({
  colors: { type: Array, required: true },
  value: { type: String, default: null },
  showColorLabel: { type: Boolean, default: false },
  variant: { type: String, default: null },
  size: { type: String, default: "large" },
  modalPosition: { type: String, default: undefined }
});

const emit = defineEmits(["change"]);
const color = ref(props.value || props.colors[0]);
const isOpened = ref(false);
const isInline = computed(() => props.variant === "inline");
const rootStyles = computed(() => ({
  "color-picker": true,
  "color-picker--inline": isInline.value,
  "color-picker--with-names": props.showColorLabel
}));

const prepareColors = computed(() =>
  props.colors.map(colorOption => {
    if (typeof colorOption === "object") return colorOption;

    return { value: colorOption, name: colorOption };
  })
);
const colorValues = computed(() => prepareColors.value.map(colorOption => colorOption.value));
const selectedColorValue = computed(() =>
  typeof color.value === "string" ? color.value : color.value?.hex || null
);
const isCustom = computed(
  () => selectedColorValue.value !== null && !colorValues.value.includes(selectedColorValue.value)
);
const disableApply = computed(() => !color.value?.hex);

const handleClose = () => {
  if (isInline.value) return;
  isOpened.value = false;
};

const lastTrackedColor = ref(null);

const trackColorSelection = (colorValue, trigger) => {
  if (!colorValue || lastTrackedColor.value === colorValue) {
    return;
  }

  const matchedColor = prepareColors.value.find(option => option.value === colorValue);
  submitDtfEvent("dtf_image_placement_color_selected", {
    trigger,
    variant: props.variant || null,
    size: props.size,
    show_color_label: props.showColorLabel,
    color_value: colorValue,
    color_name: matchedColor?.name || null,
    is_custom_color: !colorValues.value.includes(colorValue)
  });
  lastTrackedColor.value = colorValue;
};

const handleSelect = value => {
  if (isInline.value) {
    color.value = typeof value === "string" ? { hex: value } : value;
    return;
  }

  emit("change", value);
  trackColorSelection(value, "swatch");
  handleClose();
};

const handleOpen = () => {
  if (isInline.value) return;
  isOpened.value = true;
};

const handleApply = () => {
  handleSelect(color.value.hex);
};

const isCustomSelected = value => isCustom.value && selectedColorValue.value === value;

watch(
  () => color.value?.hex,
  newValue => {
    if (isInline.value) {
      emit("change", newValue);
      trackColorSelection(newValue, "inline_picker");
    }
  }
);
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.color-picker {
  $this: &;

  position: relative;

  &--inline {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 16px;

    & #{$this}__items {
      @include with-desktop {
        flex: 0 0 50%;
      }
    }

    @include with-desktop {
      flex-direction: row;
      gap: 32px;
    }
  }

  &__items {
    display: flex;
    gap: 9px;
    flex-wrap: wrap;
  }

  &--with-names {
    #{$this}__item {
      width: 72px;
      height: 70px;
      padding-top: 0;
      border: 1px solid $gray-border;
    }

    #{$this}__checkbox,
    #{$this}__icon {
      margin-bottom: 14px;
    }
  }

  &__item {
    cursor: pointer;
    appearance: none;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    padding: 8px;
    border-radius: 8px;
    border: 1px solid rgba($black, 0.2);
    background: $white;

    &--selected {
      border: 2px solid $gray-border;
      outline: 2px solid $primary-600;
    }

    &--large {
      width: 44px;
      height: 44px;

      #{$this}__checkbox {
        position: absolute;
      }
    }

    &--medium {
      width: 40px;
      height: 40px;

      #{$this}__checkbox {
        position: absolute;
      }
    }

    &--small {
      width: 36px;
      height: 36px;
    }
  }

  &__color-name {
    position: absolute;
    background: $white;
    height: 25px;
    width: 70px;
    padding: 12px 0 0;
    margin-top: 51px;
    font-size: 12px;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  &__icon {
    width: 28px;
    height: 28px;
  }

  &__modal {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: auto;
    z-index: 20;
    top: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    @include with-tablet {
      display: block;
      position: absolute;
      top: -4px;
      left: 100%;
      margin-left: -48px;
      width: 280px;
    }

    &--bottom-right {
      @include with-tablet {
        left: 45%;
      }
    }
  }

  &__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba($black, 0.3);
    z-index: -1;

    @include with-tablet {
      display: none;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-radius: 16px 16px 0 0;
    border: 1px solid $gray-border;
    background: $white;
    box-shadow: 0 3px 6px 0 rgba($black, 0.1);
    padding: 16px 16px 24px;

    @include with-tablet {
      padding: 12px 16px;
      border-radius: 8px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $text-black;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.55;

    @include with-tablet {
      font-size: 16px;
      line-height: 1.75;
    }
  }

  &__close {
    width: 24px;
    height: 24px;
    cursor: pointer;
    color: $crafterpay-gray-modern-400;

    &:hover {
      color: $black;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 24px;

    @include with-tablet {
      justify-content: space-between;
      gap: 0;
    }
  }

  &__cancel {
    appearance: none;
    border: none;
    background: $white;
    padding: 10px 16px;
    color: #353535;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.5;
  }

  &__apply {
    appearance: none;
    border: none;
    border-radius: 8px;
    background: $primary-600;
    color: $white;
    min-width: 156px;
    padding: 10px 16px;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.5;

    &:hover {
      background: $primary-650;
    }

    &--disabled {
      opacity: 0.5;
    }
  }

  &__checkbox {
    width: 30px;
    height: 30px;
    color: $text-black;
  }

  & &__selector {
    width: 100%;
    box-shadow: none;

    .vc-chrome-body {
      padding: 16px 0 0 0;
    }

    .vc-chrome-color-wrap {
      display: none;
    }

    .vc-saturation {
      border-radius: 4px;
      overflow: hidden;
    }

    .vc-chrome-hue-wrap {
      margin: 0;

      .vc-hue {
        border-radius: 100px;
      }
    }

    .vc-chrome-fields {
      .vc-input__label {
        display: none;
      }
    }

    .vc-chrome-toggle-btn {
      display: none;
    }

    .vc-editable-input {
      input {
        border-radius: 8px;
        border: 1px solid $crafterpay-gray-modern-300;
        background: $crafterpay-gray-modern-50;
        color: $crafterpay-gray-modern-700;
        height: 40px;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.43;
      }
    }
  }
}
</style>
